import React, { Component } from 'react';
import API from '../api';
import Groups from './Groups';
import Events from './Events';
import People from './People';
import Registrations from './Registrations';
import Sermons from './Sermons';
import DetailModal from './DetailModal';

const COMPONENTS = {
  groups: Groups,
  events: Events,
  people: People,
  registrations: Registrations,
  sermons: Sermons,
};

// Maps section type to detail item type
const DETAIL_TYPES = {
  groups: 'group',
  events: 'event',
  sermons: 'message',
};

interface PcoProps {
  siteUniqueName: string;
  sectionType: string;
  settings: Record<string, any>;
  host: string;
  protocol: string;
  studioMode?: boolean;
}

interface PcoState {
  data: Record<string, any> | null;
  loading: boolean;
  error: string | null;
  studioMode: boolean;
  settings: Record<string, any>;
  detailItem: { itemType: string; itemId: string } | null;
}

class PlanningCenter extends Component<PcoProps, PcoState> {
  _unmounted = false;
  _containerEl: HTMLElement | null = null;

  constructor(props: PcoProps) {
    super(props);
    this.state = {
      data: null,
      loading: true,
      error: null,
      studioMode: false,
      settings: props.settings || {},
      detailItem: null,
    };
    this._handleSettingsUpdate = this._handleSettingsUpdate.bind(this);
    this._handleItemClick = this._handleItemClick.bind(this);
    this._handleCloseDetail = this._handleCloseDetail.bind(this);
  }

  componentDidMount() {
    // Detect Studio iframe
    try {
      if (window.location !== window.parent.location &&
          window.parent.location.href.indexOf('studio.') > -1) {
        this.setState({ studioMode: true });
        this._listenForSettingsUpdates();
      }
    } catch (e) {
      // Cross-origin — not in Studio
    }

    // Always fetch from API — same codepath for all contexts
    this.fetchData();
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._containerEl) {
      this._containerEl.removeEventListener('pco-settings-update', this._handleSettingsUpdate as EventListener);
    }
  }

  _findContainer() {
    const el = document.querySelector(`.pco-content[data-pco-type="${this.props.sectionType}"]`);
    this._containerEl = el as HTMLElement | null;
    return el;
  }

  _listenForSettingsUpdates() {
    const container = this._findContainer();
    if (container) {
      container.addEventListener('pco-settings-update', this._handleSettingsUpdate as EventListener);
    }
  }

  _handleSettingsUpdate(e: CustomEvent) {
    if (this._unmounted) return;
    const newSettings = e.detail || {};
    this.setState({ settings: { ...newSettings, _studioMode: true } });
  }

  _handleItemClick(itemType: string, itemId: string) {
    if (this.state.studioMode) return;
    this.setState({ detailItem: { itemType, itemId } });
  }

  _handleCloseDetail() {
    this.setState({ detailItem: null });
  }

  fetchData() {
    const { siteUniqueName, sectionType, settings, host, protocol } = this.props;
    const p = protocol || 'https';
    const h = host || 'orbitalsites.com';

    const params: Record<string, any> = {};
    if (settings) {
      if (settings['pco-count'] && settings['pco-count'] !== 'all') params.count = settings['pco-count'];
      if (settings['pco-group-type'] && settings['pco-group-type'] !== 'all') params.group_type = settings['pco-group-type'];
      if (settings['pco-membership-type'] && settings['pco-membership-type'] !== 'all') params.membership_type = settings['pco-membership-type'];
    }

    API.getPcoData(p, h, siteUniqueName, sectionType, params)
      .then((json: any) => {
        if (this._unmounted) return;
        this.setState({ data: json.data || [], loading: false });
        document.dispatchEvent(new CustomEvent('section-content-loaded'));
      })
      .catch(err => {
        if (this._unmounted) return;
        console.error('PCO fetch error:', err);
        this.setState({ error: 'Failed to load content', loading: false });
        document.dispatchEvent(new CustomEvent('section-content-loaded'));
      });
  }

  render() {
    const { sectionType, siteUniqueName, host, protocol } = this.props;
    const { data, loading, error, studioMode, settings, detailItem } = this.state;
    const SubComponent = (COMPONENTS as Record<string, React.ComponentType<any>>)[sectionType];
    const detailType = (DETAIL_TYPES as Record<string, string>)[sectionType];

    const effectiveSettings = studioMode
      ? { ...settings, _studioMode: true }
      : (this.props.settings || {});

    return (
      <div className="pco-content-inner" data-pco-type={sectionType}>
        {loading && (
          <div className="pco-empty"><p>Loading...</p></div>
        )}
        {error && (
          <div className="pco-empty"><p>{error}</p></div>
        )}
        {!loading && !error && SubComponent && (
          <SubComponent
            data={data}
            settings={effectiveSettings}
            onItemClick={detailType ? (id: string) => this._handleItemClick(detailType, id) : null}
          />
        )}
        {detailItem && (
          <DetailModal
            itemType={detailItem.itemType}
            itemId={detailItem.itemId}
            siteUniqueName={siteUniqueName}
            host={host}
            protocol={protocol}
            onClose={this._handleCloseDetail}
          />
        )}
      </div>
    );
  }
}

export default PlanningCenter;
